import { cn } from "@/lib/cn";
import { IconMinus } from "@tabler/icons-react";
import { OTPInput, OTPInputContext } from "input-otp";
import * as React from "react";

/**
 * shadcn-style wrapper around the input-otp package, themed for the Storydoc
 * design system: joined 36px cells per group, primary focus ring, dash
 * separator. A single hidden input backs all slots, so paste, autofill
 * (one-time-code) and screen readers work natively.
 */
const InputOTP = React.forwardRef<
  React.ElementRef<typeof OTPInput>,
  React.ComponentPropsWithoutRef<typeof OTPInput>
>(({ className, containerClassName, ...props }, ref) => (
  <OTPInput
    ref={ref}
    containerClassName={cn(
      "tw-flex tw-items-center tw-gap-2 has-[:disabled]:tw-opacity-50",
      containerClassName,
    )}
    className={cn("disabled:tw-cursor-not-allowed", className)}
    {...props}
  />
));
InputOTP.displayName = "InputOTP";

const InputOTPGroup = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "tw-isolate tw-flex tw-rounded-md tw-shadow-xs tw-outline tw-outline-1 tw-outline-blue/5",
      className,
    )}
    {...props}
  />
));
InputOTPGroup.displayName = "InputOTPGroup";

const InputOTPSlot = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div"> & { index: number }
>(({ index, className, ...props }, ref) => {
  const inputOTPContext = React.useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = inputOTPContext.slots[index];

  return (
    <div
      ref={ref}
      className={cn(
        "tw-relative tw-flex tw-size-9 tw-items-center tw-justify-center tw-bg-white tw-text-sm tw-leading-5 tw-text-blue first:tw-rounded-l-md last:tw-rounded-r-md [&:not(:first-child)]:tw-border-l [&:not(:first-child)]:tw-border-blue-100",
        isActive && "tw-z-10 tw-shadow-[0px_0px_0px_1px_#610cff]",
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
          <div className="tw-h-4 tw-w-px tw-animate-pulse tw-bg-blue" />
        </div>
      )}
    </div>
  );
});
InputOTPSlot.displayName = "InputOTPSlot";

const InputOTPSeparator = React.forwardRef<
  React.ElementRef<"div">,
  React.ComponentPropsWithoutRef<"div">
>(({ ...props }, ref) => (
  <div ref={ref} role="separator" {...props}>
    <IconMinus size={24} className="tw-text-blue" />
  </div>
));
InputOTPSeparator.displayName = "InputOTPSeparator";

export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };
